
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreVertical, UserCog, UserX, Trash2, UserCheck } from 'lucide-react';
import { TeamMember } from '../hooks/useTeamData';

interface TeamMemberActionsMenuProps {
  member: TeamMember;
  isCurrentUser: boolean;
  onMakeAdmin: () => void;
  onRemoveAdmin: () => void;
  onRemoveMember: () => void;
  onReactivateMember: () => void;
  onSuspendMember: () => void;
  onUnsuspendMember: () => void;
}

const TeamMemberActionsMenu = ({
  member,
  isCurrentUser,
  onMakeAdmin,
  onRemoveAdmin,
  onRemoveMember,
  onReactivateMember,
  onSuspendMember,
  onUnsuspendMember
}: TeamMemberActionsMenuProps) => {
  // Don't show actions menu for current user
  if (isCurrentUser) return null;

  const canPromoteToAdmin = member.role !== 'admin' && member.status !== 'deactivated';
  const canDemoteFromAdmin = member.role === 'admin' && member.status !== 'deactivated';
  const isSuspended = member.status === 'suspended';
  const isDeactivated = member.status === 'deactivated';
  const canSuspend = member.status === 'active'; // Only active users can be suspended
  const canUnsuspend = member.status === 'suspended' && !isDeactivated; // Can't unsuspend deactivated users

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {isDeactivated ? (
          <DropdownMenuItem onClick={onReactivateMember} className="text-green-600">
            <UserCheck className="mr-2 h-4 w-4" />
            Reactivate Member
          </DropdownMenuItem>
        ) : (
          <>
            {canPromoteToAdmin && (
              <DropdownMenuItem onClick={onMakeAdmin}>
                <UserCog className="mr-2 h-4 w-4" />
                Make Company Admin
              </DropdownMenuItem>
            )}
            
            {canDemoteFromAdmin && (
              <DropdownMenuItem onClick={onRemoveAdmin}>
                <UserX className="mr-2 h-4 w-4" />
                Remove Admin Role
              </DropdownMenuItem>
            )}

            {(canPromoteToAdmin || canDemoteFromAdmin) && <DropdownMenuSeparator />}

            {canSuspend && (
              <DropdownMenuItem onClick={onSuspendMember} className="text-amber-600">
                <UserX className="mr-2 h-4 w-4" />
                Suspend User
              </DropdownMenuItem>
            )}
            
            {canUnsuspend && (
              <DropdownMenuItem onClick={onUnsuspendMember} className="text-green-600">
                <UserCheck className="mr-2 h-4 w-4" />
                Remove Suspension
              </DropdownMenuItem>
            )}

            {(canSuspend || canUnsuspend) && <DropdownMenuSeparator />}
            
            <DropdownMenuItem onClick={onRemoveMember} className="text-red-600">
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate Member
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TeamMemberActionsMenu;
